import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

interface ChangeLevelNumberModalProps {
    show: boolean;
    onHide: () => void;
    currentNumber: number;
    existingNumbers: number[];
    onSave: (newNumber: number) => Promise<void>;
}

export const ChangeLevelNumberModal: React.FC<ChangeLevelNumberModalProps> = ({
    show,
    onHide,
    currentNumber,
    existingNumbers,
    onSave
}) => {
    const [number, setNumber] = useState<string>(String(currentNumber));
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show) {
            setNumber(String(currentNumber));
            setError(null);
        }
    }, [show, currentNumber]);

    const handleClose = () => {
        if (!submitting) {
            setError(null);
            onHide();
        }
    };

    const handleSave = async () => {
        setError(null);

        const parsed = parseInt(number, 10);

        if (isNaN(parsed)) {
            setError('Våningsnummer måste vara ett heltal');
            return;
        }

        if (existingNumbers.includes(parsed)) {
            setError('Detta våningsnummer används redan av en annan våning');
            return;
        }

        if (parsed === currentNumber) {
            onHide();
            return;
        }

        setSubmitting(true);
        try {
            await onSave(parsed);
            onHide();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Kunde inte spara ändringen';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Ändra våningsnummer</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                    <Form.Group>
                        <Form.Label>Nytt våningsnummer</Form.Label>
                        <Form.Control
                            type="number"
                            step="1"
                            value={number}
                            onChange={(e) => setNumber(e.target.value)}
                            autoFocus
                            disabled={submitting}
                        />
                    </Form.Group>
                    {error && (
                        <div className="text-danger mt-2">{error}</div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                    Avbryt
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={submitting}>
                    {submitting ? 'Sparar...' : 'Spara'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
